import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FileDown, Settings, Link2, Copy, Edit, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import CatalogView from "./CatalogView";
import CatalogProductDialog from "./CatalogProductDialog";
import CatalogConfigDialog from "./CatalogConfigDialog";
import ProductPhotoUpload from "./ProductPhotoUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exportPdf = async (node, filename) => {
  const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff", windowWidth: node.scrollWidth });
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const pagePx = Math.floor((canvas.width * ph) / pw);
  for (let y = 0, page = 0; y < canvas.height; y += pagePx, page++) {
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = Math.min(pagePx, canvas.height - y);
    slice.getContext("2d").drawImage(canvas, 0, y, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pw, (slice.height * pw) / canvas.width);
  }
  pdf.save(filename);
};

const PublicLinkBar = ({ config, onConfig }) => {
  const url = `${window.location.origin}/catalogo/${config.public_token}`;
  if (!config.public_enabled) {
    return <div className="text-xs bg-gray-50 border rounded p-2 flex-1">Link público desativado. Ative em <b>Configurar</b> para compartilhar o catálogo online.</div>;
  }
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Link copiado"); };
  return (
    <div className="flex items-center gap-2 flex-1 bg-emerald-50 border border-emerald-300 rounded p-2 text-xs" data-testid="catalog-public-bar">
      <Link2 className="h-4 w-4" />
      <a href={url} target="_blank" rel="noreferrer" className="underline truncate" data-testid="catalog-public-link">{url}</a>
      <Button size="sm" variant="outline" onClick={copy} data-testid="catalog-copy-link"><Copy className="h-3 w-3 mr-1" />Copiar</Button>
      <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Confira nosso catálogo: ${url}`)}`, "_blank")} data-testid="catalog-share-whatsapp"><MessageCircle className="h-3 w-3 mr-1" />WhatsApp</Button>
      <Button size="sm" variant="ghost" onClick={onConfig}>Ajustar</Button>
    </div>
  );
};

const ProductsTable = ({ companyId, products, onEdit, onChanged }) => (
  <div className="border rounded-lg overflow-x-auto">
    <table className="w-full text-sm min-w-[700px]">
      <thead className="bg-gray-100">
        <tr><th className="text-left p-2">Produto</th><th className="text-left p-2">Destaque</th><th className="text-left p-2">Foto</th><th className="text-center p-2">No catálogo</th><th className="text-center p-2">Ações</th></tr>
      </thead>
      <tbody>
        {products.map((p) => (
          <tr key={p.id} className="border-t">
            <td className="p-2 font-medium">{p.code ? `${p.code} · ` : ""}{p.name}</td>
            <td className="p-2 text-xs">{p.catalog_headline || "—"}</td>
            <td className="p-2"><ProductPhotoUpload key={`${p.id}-${p.catalog_photo_id || ""}`} companyId={companyId} product={p} onChanged={onChanged} size="sm" /></td>
            <td className="p-2 text-center">{p.catalog_visible === false ? <Badge variant="outline">Oculto</Badge> : <Badge>Visível</Badge>}</td>
            <td className="p-2 text-center"><Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`catalog-edit-${p.id}`}><Edit className="h-3 w-3 mr-1" />Editar</Button></td>
          </tr>
        ))}
        {products.length === 0 && <tr><td colSpan={5} className="p-6 text-center">Cadastre produtos na aba Produtos.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function CatalogTab({ companyId, companyName, products = [], onProductsChanged }) {
  const [priceType, setPriceType] = useState("varejo");
  const [freightValue, setFreightValue] = useState("");
  const [freightNote, setFreightNote] = useState("");
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const previewRef = useRef(null);

  const load = () => {
    axios.get(`${API}/companies/${companyId}/factory/catalog?price_type=${priceType}`).then((r) => setData(r.data));
    axios.get(`${API}/companies/${companyId}/factory/catalog/config`).then((r) => setConfig(r.data));
  };
  useEffect(() => { if (companyId) load(); }, [companyId, priceType]);

  const refresh = () => { load(); onProductsChanged && onProductsChanged(); };
  const withPhoto = (p) => ({ ...p, photo_url: p.catalog_photo_id ? `/api/public/catalog-photo/${p.catalog_photo_id}` : null });

  const pdf = async () => {
    if (!previewRef.current) return;
    setExporting(true);
    try {
      await exportPdf(previewRef.current, `catalogo_${(companyName || "fabrica").replace(/[^a-z0-9]+/gi, "_").toLowerCase()}_${priceType}.pdf`);
      toast.success("PDF do catálogo gerado");
    } catch (e) { toast.error("Erro ao gerar PDF: " + (e.message || "")); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-3" data-testid="catalog-tab">
      <div className="flex flex-wrap items-end gap-2 border rounded-lg p-3 bg-slate-50">
        <div>
          <label className="text-xs font-bold block">Tabela de preço</label>
          <Select value={priceType} onValueChange={setPriceType}>
            <SelectTrigger className="w-36" data-testid="catalog-price-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="varejo">Varejo</SelectItem>
              <SelectItem value="atacado">Atacado</SelectItem>
              <SelectItem value="especial">Especial</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold block">Frete (R$)</label>
          <Input type="number" step="0.01" className="w-32" value={freightValue} onChange={(e) => setFreightValue(e.target.value)} placeholder="0,00" data-testid="catalog-freight-value" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-bold block">Observação do frete</label>
          <Input value={freightNote} onChange={(e) => setFreightNote(e.target.value)} placeholder="Ex.: Entrega em Maceió, até 3 dias úteis" data-testid="catalog-freight-note" />
        </div>
        <Button variant="outline" onClick={() => setCfgOpen(true)} disabled={!config} data-testid="catalog-open-config"><Settings className="h-4 w-4 mr-1" />Configurar</Button>
        <Button onClick={pdf} disabled={exporting || !data} data-testid="catalog-export-pdf"><FileDown className="h-4 w-4 mr-1" />{exporting ? "Gerando..." : "Gerar PDF"}</Button>
      </div>
      {config && <PublicLinkBar config={config} onConfig={() => setCfgOpen(true)} />}
      <ProductsTable companyId={companyId} products={products.map(withPhoto)} onEdit={(p) => setEditing(p)} onChanged={refresh} />
      <div className="border rounded-lg overflow-hidden shadow-sm">
        <div className="bg-gray-100 px-3 py-1 text-xs font-bold">Pré-visualização</div>
        <div ref={previewRef} className="max-w-[900px] mx-auto">
          <CatalogView data={data} freight={{ value: parseFloat(freightValue) || 0, note: freightNote }} />
        </div>
      </div>
      {editing && <CatalogProductDialog companyId={companyId} product={withPhoto(products.find((p) => p.id === editing.id) || editing)} onClose={() => setEditing(null)} onSaved={refresh} />}
      {cfgOpen && config && <CatalogConfigDialog companyId={companyId} config={config} onClose={() => setCfgOpen(false)} onSaved={(c) => { setConfig(c); load(); }} />}
    </div>
  );
}
